import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs';
import {
  SessionGrant,
  ShareSessionResponse,
  UserSession,
  UserSessionsService
} from '../../services/api-services/user-sessions.service';

export interface ShareSessionModalData {
  session: UserSession;
  isOwner?: boolean;
}

@Component({
  selector: 'app-share-session-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatChipsModule
  ],
  templateUrl: './share-session-modal.component.html',
  styleUrl: './share-session-modal.component.scss'
})
export class ShareSessionModalComponent implements OnInit {
  grants: SessionGrant[] = [];
  isLoadingGrants = false;
  grantsError: string | null = null;

  // Add-user form
  addForm: FormGroup;
  isSharing = false;
  shareError: string | null = null;
  notFoundUsers: string[] = [];

  // Make-public toggle
  isMakingPublic = false;

  // Per-row revoke tracking
  private revoking = new Set<string>();
  private hasChanges = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly sessionsService: UserSessionsService,
    private readonly dialogRef: MatDialogRef<ShareSessionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareSessionModalData
  ) {
    this.addForm = this.fb.group({
      identifiers: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadGrants();
  }

  loadGrants(): void {
    this.isLoadingGrants = true;
    this.grantsError = null;
    this.sessionsService
      .getSessionGrants(this.data.session.id)
      .pipe(finalize(() => (this.isLoadingGrants = false)))
      .subscribe({
        next: grants => (this.grants = grants),
        error: () => (this.grantsError = 'Failed to load access list.')
      });
  }

  get publicGrant(): SessionGrant | undefined {
    return this.grants.find(g => g.is_public);
  }

  get userGrants(): SessionGrant[] {
    return this.grants.filter(g => !g.is_public);
  }

  shareWithUsers(): void {
    if (this.addForm.invalid || this.isSharing) return;

    const raw: string = this.addForm.value.identifiers;
    const usernames = raw
      .split(/[\s,;]+/)
      .map(s => s.trim())
      .filter(Boolean);

    if (!usernames.length) return;

    this.isSharing = true;
    this.shareError = null;
    this.notFoundUsers = [];

    this.sessionsService
      .shareSession(this.data.session.id, {
        usernames,
        can_write: true
      })
      .pipe(finalize(() => (this.isSharing = false)))
      .subscribe({
        next: (res: ShareSessionResponse) => {
          this.hasChanges = this.hasChanges || res.grants_created > 0;
          this.notFoundUsers = res.not_found ?? [];
          if (this.notFoundUsers.length === 0) {
            this.addForm.reset({ identifiers: '', can_write: true });
          } else {
            this.shareError = `Could not find: ${this.notFoundUsers.join(', ')}`;
          }
          this.loadGrants();
        },
        error: () => (this.shareError = 'Failed to share session. Please try again.')
      });
  }

  makePublic(): void {
    if (this.isMakingPublic) return;
    this.isMakingPublic = true;
    this.sessionsService
      .shareSession(this.data.session.id, { public: true, can_write: false })
      .pipe(finalize(() => (this.isMakingPublic = false)))
      .subscribe({
        next: () => {
          this.hasChanges = true;
          this.loadGrants();
        },
        error: () => (this.shareError = 'Failed to make session public.')
      });
  }

  revokeGrant(username: string): void {
    if (this.revoking.has(username)) return;
    this.revoking.add(username);
    this.sessionsService
      .revokeSessionGrant(this.data.session.id, username)
      .pipe(finalize(() => this.revoking.delete(username)))
      .subscribe({
        next: () => {
          this.hasChanges = true;
          this.loadGrants();
        },
        error: () => (this.shareError = `Failed to revoke access for "${username}".`)
      });
  }

  isRevoking(username: string): boolean {
    return this.revoking.has(username);
  }

  close(): void {
    this.dialogRef.close(this.hasChanges);
  }
}
